import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

const SESSION_FLAG = 'division240_reminder_shown';

// Référence des équipements requis par catégorie de navigation (cf. Division240Selector)
const EQUIPEMENTS_BASIQUE = [
  'Équipement individuel de flottabilité',
  'Dispositif lumineux',
  "Moyens mobiles de lutte contre l'incendie",
  "Dispositif d'assèchement manuel",
  'Dispositif de remorquage',
  'Ligne de mouillage',
  'Annuaire des marées',
  "Dispositif de repérage et d'assistance personne à la mer (bouée)",
];
const EQUIPEMENTS_COTIER = [
  '3 feux rouges à main',
  'Compas magnétique (ou GPS)',
  'Cartes marines officielles',
];
const EQUIPEMENTS_SEMI_HAUTURIER = [
  'Pavillon national',
  'Règlement RIPAM',
  'Description du système de balisage',
  'Livre des feux tenu à jour',
  'Harnais et longe',
  'VHF fixe',
];
const EQUIPEMENTS_HAUTURIER = [
  'Radeau de survie',
  'Matériel pour faire le point',
  'Journal de bord',
  'Dispositif de réception météo',
  'Trousse de secours',
  'Dispositif lumineux recherche de nuit',
  'Radiobalise EPIRB',
  'VHF fixe + VHF portative',
];

const getEquipementsRequis = (categorie: string): string[] => {
  if (categorie.includes('Hauturier') && !categorie.includes('Semi'))
    return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER, ...EQUIPEMENTS_SEMI_HAUTURIER, ...EQUIPEMENTS_HAUTURIER];
  if (categorie.includes('Semi-hauturier'))
    return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER, ...EQUIPEMENTS_SEMI_HAUTURIER];
  if (categorie.includes('Côtier')) return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER];
  if (categorie.includes('Basique')) return [...EQUIPEMENTS_BASIQUE];
  return [];
};

interface BateauManquants {
  bateauNom: string;
  bateauId: string;
  categorie: string;
  manquants: string[];
}

const Division240Reminder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [bateauxAvecManquants, setBateauxAvecManquants] = useState<BateauManquants[]>([]);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem(SESSION_FLAG) === '1') return;

    const checkEquipements = async () => {
      try {
        const { data: bateaux } = await supabase
          .from('bateaux')
          .select('id, nom')
          .eq('user_id', user.id);

        if (!bateaux || bateaux.length === 0) return;

        const { data: articles } = await supabase
          .from('inventaire')
          .select('nom, sous_categorie, bateau_id, quantite')
          .eq('user_id', user.id)
          .eq('categorie', 'Sécurité');

        const resultats: BateauManquants[] = [];

        for (const bateau of bateaux) {
          const articlesBateau = (articles || []).filter(a => a.bateau_id === bateau.id);
          // Détecter la catégorie via les sous-catégories Division 240 déjà présentes
          const categoriesPresentes = Array.from(
            new Set(
              articlesBateau
                .map(a => a.sous_categorie || '')
                .filter(sc => sc.startsWith('Division 240 -'))
            )
          );

          if (categoriesPresentes.length === 0) continue; // pas encore configuré → on ne harcèle pas

          // Prendre la catégorie la plus large déjà sélectionnée
          const ordre = ['Basique', 'Côtier', 'Semi-hauturier', 'Hauturier'];
          const categoriePlusLarge = categoriesPresentes
            .map(sc => sc.replace('Division 240 - ', ''))
            .sort((a, b) => ordre.indexOf(b) - ordre.indexOf(a))[0];

          const requis = getEquipementsRequis(categoriePlusLarge);
          const nomsPresents = articlesBateau
            .filter(a => a.quantite > 0)
            .map(a => a.nom.toLowerCase());

          const manquants = requis.filter(eq => !nomsPresents.includes(eq.toLowerCase()));

          if (manquants.length > 0) {
            resultats.push({
              bateauNom: bateau.nom,
              bateauId: bateau.id,
              categorie: categoriePlusLarge,
              manquants,
            });
          }
        }

        if (resultats.length > 0) {
          setBateauxAvecManquants(resultats);
          setOpen(true);
          sessionStorage.setItem(SESSION_FLAG, '1');
        }
      } catch (err) {
        console.error('Division240Reminder: error', err);
      }
    };

    // léger délai pour laisser l'UI se charger
    const t = setTimeout(checkEquipements, 2500);
    return () => clearTimeout(t);
  }, [user]);

  const handleVoirInventaire = () => {
    setOpen(false);
    navigate('/inventory');
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="border-2 border-nautic-gold bg-gradient-to-br from-background to-nautic-gold/5">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-nautic-gold/20 border border-nautic-gold/40">
              <AlertTriangle className="h-6 w-6 text-nautic-gold" />
            </div>
            <Badge variant="outline" className="border-nautic-gold text-nautic-gold bg-nautic-gold/10">
              Division 240
            </Badge>
          </div>
          <AlertDialogTitle className="text-nautic">
            Matériel de sécurité incomplet
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Pour votre sécurité, voici les équipements obligatoires manquants à votre inventaire :
              </p>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {bateauxAvecManquants.map((b, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-nautic-gold/30 bg-nautic-gold/5 p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-foreground">⛵ {b.bateauNom}</p>
                      <Badge variant="outline" className="text-xs">{b.categorie}</Badge>
                    </div>
                    <ul className="space-y-1">
                      {b.manquants.slice(0, 5).map((eq, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Shield className="h-3 w-3 text-nautic-gold mt-0.5 shrink-0" />
                          <span>{eq}</span>
                        </li>
                      ))}
                      {b.manquants.length > 5 && (
                        <li className="text-xs italic text-nautic-gold">
                          + {b.manquants.length - 5} autre(s) équipement(s)…
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Plus tard</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleVoirInventaire}
            className="bg-nautic-gold text-nautic hover:bg-nautic-lightgold font-semibold"
          >
            Compléter mon inventaire
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default Division240Reminder;
